import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface ChangeOrderLineItem {
  itemId: string;
  itemType: "addition" | "removal" | "modification";
  category: "labor" | "material" | "equipment" | "service" | "other";
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  originalBudgetItem?: string;
  reason: string;
  notes: string;
}

interface ImpactAnalysis {
  scheduleImpact: {
    delayDays: number;
    criticalPath: boolean;
    milestoneAffected: string[];
    newCompletionDate?: string;
  };
  costImpact: {
    originalBudget: number;
    adjustmentAmount: number;
    newBudget: number;
    contingencyUsed: number;
    percentageChange: number;
  };
  resourceImpact: {
    additionalLabor: boolean;
    equipmentChanges: boolean;
    materialChanges: boolean;
    subcontractorChanges: boolean;
    permitsRequired: boolean;
  };
  qualityImpact: {
    specsAffected: boolean;
    testingRequired: boolean;
    complianceImpact: boolean;
    riskAssessment: string;
  };
}

interface ChangeOrderData {
  changeOrderNumber: string;
  jobId: string;
  customerId: string;
  originalContractValue: number;
  changeType: "scope_addition" | "scope_reduction" | "design_change" | "field_condition" | "owner_request" | "regulatory_change";
  priority: "low" | "medium" | "high" | "urgent";
  initiatedBy: string;
  dateInitiated: string;
  requestedBy: string;
  title: string;
  description: string;
  justification: string;
  lineItems: ChangeOrderLineItem[];
  impactAnalysis: ImpactAnalysis;
  attachments: {
    drawings?: string[];
    photos?: string[];
    specifications?: string[];
    correspondence?: string[];
    calculations?: string[];
  };
  approvals: {
    estimator: {
      approved: boolean;
      approvedBy?: string;
      approvalDate?: string;
      comments?: string;
    };
    projectManager: {
      approved: boolean;
      approvedBy?: string;
      approvalDate?: string;
      comments?: string;
    };
    customer: {
      approved: boolean;
      approvedBy?: string;
      approvalDate?: string;
      comments?: string;
      signedDocument?: string;
    };
  };
  negotiation: {
    customerProposal?: number;
    negotiatedAmount?: number;
    paymentTerms?: string;
    scheduleAgreement?: string;
    specialConditions?: string[];
  };
  status: "draft" | "estimating" | "pending_internal_approval" | "pending_customer_approval" | "negotiating" | "approved" | "rejected" | "executed";
  notes: string;
}

class ChangeOrderWorkflowStore {
  rootStore: RootStore;

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Form Data
  formData: ChangeOrderData = {
    changeOrderNumber: "",
    jobId: "",
    customerId: "",
    originalContractValue: 0,
    changeType: "scope_addition",
    priority: "medium",
    initiatedBy: "",
    dateInitiated: new Date().toISOString().split("T")[0],
    requestedBy: "",
    title: "",
    description: "",
    justification: "",
    lineItems: [],
    impactAnalysis: {
      scheduleImpact: {
        delayDays: 0,
        criticalPath: false,
        milestoneAffected: [],
      },
      costImpact: {
        originalBudget: 0,
        adjustmentAmount: 0,
        newBudget: 0,
        contingencyUsed: 0,
        percentageChange: 0,
      },
      resourceImpact: {
        additionalLabor: false,
        equipmentChanges: false,
        materialChanges: false,
        subcontractorChanges: false,
        permitsRequired: false,
      },
      qualityImpact: {
        specsAffected: false,
        testingRequired: false,
        complianceImpact: false,
        riskAssessment: "",
      },
    },
    attachments: {},
    approvals: {
      estimator: {
        approved: false,
      },
      projectManager: {
        approved: false,
      },
      customer: {
        approved: false,
      },
    },
    negotiation: {},
    status: "draft",
    notes: "",
  };

  // Lookup Data
  jobs: any[] = [];
  customers: any[] = [];
  milestones: any[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.formData.changeOrderNumber = this.generateChangeOrderNumber();
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  setError = (message: string | null) => {
    this.error = message;
  };

  setSuccess = (message: string | null) => {
    this.success = message;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    if (this.activeStep > 0) {
      this.activeStep--;
    }
  };

  updateFormData = <K extends keyof ChangeOrderData>(
    field: K,
    value: ChangeOrderData[K]
  ) => {
    this.formData[field] = value;
    if (field === "lineItems") {
      this.calculateImpact();
    }
    if (field === "jobId") {
      this.loadJobDetails();
    }
  };

  updateImpactAnalysis = <K extends keyof ImpactAnalysis>(
    section: K,
    value: ImpactAnalysis[K]
  ) => {
    this.formData.impactAnalysis[section] = value;
    this.calculateImpact();
  };

  updateApproval = <K extends keyof ChangeOrderData["approvals"]>(
    approver: K,
    value: ChangeOrderData["approvals"][K]
  ) => {
    this.formData.approvals[approver] = value;
  };

  updateNegotiation = <K extends keyof ChangeOrderData["negotiation"]>(
    field: K,
    value: ChangeOrderData["negotiation"][K]
  ) => {
    this.formData.negotiation[field] = value;
  };

  addLineItem = (item: ChangeOrderLineItem) => {
    this.formData.lineItems.push(item);
    this.calculateImpact();
  };

  removeLineItem = (index: number) => {
    this.formData.lineItems.splice(index, 1);
    this.calculateImpact();
  };

  updateLineItem = (index: number, item: ChangeOrderLineItem) => {
    this.formData.lineItems[index] = item;
    this.calculateImpact();
  };

  generateChangeOrderNumber = (): string => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `CO-${year}${month}${day}-${random}`;
  };

  generateLineItemId = (): string => {
    return `LI-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  loadJobDetails = async () => {
    if (!this.formData.jobId) return;

    try {
      // Load job-specific data like original contract value, customer, etc.
      const job = this.jobs.find(j => j.id === this.formData.jobId);
      if (job) {
        this.formData.customerId = job.customerId || "";
        this.formData.originalContractValue = job.contractValue || 0;
        this.formData.impactAnalysis.costImpact.originalBudget = job.contractValue || 0;

        // Load milestones for this job
        this.milestones = [
          { id: "1", name: "Design Complete", date: "2024-02-15" },
          { id: "2", name: "Materials Delivery", date: "2024-03-01" },
          { id: "3", name: "Installation Start", date: "2024-03-15" },
          { id: "4", name: "Testing & Commissioning", date: "2024-04-01" },
          { id: "5", name: "Project Completion", date: "2024-04-15" },
        ];
      }
    } catch (error: any) {
      this.error = error.message || "Failed to load job details";
    }
  };

  calculateImpact = () => {
    // Calculate cost impact
    const totalAdjustment = this.formData.lineItems.reduce((sum, item) => {
      return sum + (item.itemType === "removal" ? -item.totalPrice : item.totalPrice);
    }, 0);

    this.formData.impactAnalysis.costImpact.adjustmentAmount = totalAdjustment;
    this.formData.impactAnalysis.costImpact.newBudget =
      this.formData.impactAnalysis.costImpact.originalBudget + totalAdjustment;

    if (this.formData.impactAnalysis.costImpact.originalBudget > 0) {
      this.formData.impactAnalysis.costImpact.percentageChange =
        (totalAdjustment / this.formData.impactAnalysis.costImpact.originalBudget) * 100;
    }

    // Calculate schedule impact
    if (this.formData.impactAnalysis.scheduleImpact.delayDays > 0) {
      const currentDate = new Date();
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + this.formData.impactAnalysis.scheduleImpact.delayDays);
      this.formData.impactAnalysis.scheduleImpact.newCompletionDate = newDate.toISOString().split("T")[0];
    }
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.error = null;

    switch (this.activeStep) {
      case 0:
        if (!this.formData.jobId) {
          this.error = "Please select a job/project";
          return false;
        }
        if (!this.formData.title) {
          this.error = "Please provide a change order title";
          return false;
        }
        if (!this.formData.description) {
          this.error = "Please provide a detailed description";
          return false;
        }
        if (!this.formData.justification) {
          this.error = "Please provide justification for this change";
          return false;
        }
        return true;

      case 1:
        if (this.formData.lineItems.length === 0) {
          this.error = "Please add at least one line item";
          return false;
        }

        const invalidItems = this.formData.lineItems.some(
          (item) => !item.description || item.quantity <= 0 || item.unitPrice < 0
        );
        if (invalidItems) {
          this.error = "Please complete all line item details";
          return false;
        }
        return true;

      case 2:
        if (!this.formData.impactAnalysis.qualityImpact.riskAssessment) {
          this.error = "Please provide a risk assessment";
          return false;
        }
        return true;

      case 3:
        if (this.formData.status === "pending_customer_approval" && !this.formData.approvals.projectManager.approved) {
          this.error = "Project manager approval is required before customer approval";
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // API Actions
  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const accountsStore = this.rootStore.accountsStore as ServiceStore;

      // Load jobs
      if (jobStore) {
        await jobStore.find({
          query: {
            $limit: 100,
            status: "active",
          },
        });
      }

      // Load customers/accounts
      if (accountsStore) {
        await accountsStore.find({
          query: {
            $limit: 100,
            active: true,
          },
        });
      }

      runInAction(() => {
        this.jobs = jobStore?.entities || [];
        this.customers = accountsStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load initial data";
        this.loading = false;
      });
    }
  };

  submitChangeOrder = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      // Create change order record
      const changeOrderData = {
        ...this.formData,
        submittedAt: new Date().toISOString(),
        submittedBy: this.rootStore.authStore?.user?.id,
        status: "estimating",
      };

      // API call would go here
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulated API call

      runInAction(() => {
        this.success = "Change order submitted successfully!";
        this.loading = false;

        // Reset form after successful submission
        setTimeout(() => {
          this.reset();
          this.rootStore.routerStore.goTo("Dashboard");
        }, 2000);
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to submit change order";
        this.loading = false;
      });
    }
  };

  // Computed Values
  get users() {
    return this.rootStore.userStore?.entities || [];
  }

  get selectedJob() {
    return this.jobs.find((j) => j.id === this.formData.jobId);
  }

  get selectedCustomer() {
    return this.customers.find((c) => c.id === this.formData.customerId);
  }

  get totalLineItemValue() {
    return this.formData.lineItems.reduce((sum, item) => {
      return sum + (item.itemType === "removal" ? -item.totalPrice : item.totalPrice);
    }, 0);
  }

  get additionItems() {
    return this.formData.lineItems.filter(item => item.itemType === "addition");
  }

  get removalItems() {
    return this.formData.lineItems.filter(item => item.itemType === "removal");
  }

  get modificationItems() {
    return this.formData.lineItems.filter(item => item.itemType === "modification");
  }

  get requiresUrgentApproval() {
    return this.formData.priority === "urgent" ||
           Math.abs(this.formData.impactAnalysis.costImpact.percentageChange) > 20 ||
           this.formData.impactAnalysis.scheduleImpact.delayDays > 30;
  }

  get allApprovalsReceived() {
    return this.formData.approvals.estimator.approved &&
           this.formData.approvals.projectManager.approved &&
           this.formData.approvals.customer.approved;
  }

  get isHighValue() {
    return Math.abs(this.totalLineItemValue) > 50000;
  }

  get hasScheduleImpact() {
    return this.formData.impactAnalysis.scheduleImpact.delayDays > 0 ||
           this.formData.impactAnalysis.scheduleImpact.criticalPath;
  }

  // Reset
  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.formData = {
      changeOrderNumber: this.generateChangeOrderNumber(),
      jobId: "",
      customerId: "",
      originalContractValue: 0,
      changeType: "scope_addition",
      priority: "medium",
      initiatedBy: "",
      dateInitiated: new Date().toISOString().split("T")[0],
      requestedBy: "",
      title: "",
      description: "",
      justification: "",
      lineItems: [],
      impactAnalysis: {
        scheduleImpact: {
          delayDays: 0,
          criticalPath: false,
          milestoneAffected: [],
        },
        costImpact: {
          originalBudget: 0,
          adjustmentAmount: 0,
          newBudget: 0,
          contingencyUsed: 0,
          percentageChange: 0,
        },
        resourceImpact: {
          additionalLabor: false,
          equipmentChanges: false,
          materialChanges: false,
          subcontractorChanges: false,
          permitsRequired: false,
        },
        qualityImpact: {
          specsAffected: false,
          testingRequired: false,
          complianceImpact: false,
          riskAssessment: "",
        },
      },
      attachments: {},
      approvals: {
        estimator: {
          approved: false,
        },
        projectManager: {
          approved: false,
        },
        customer: {
          approved: false,
        },
      },
      negotiation: {},
      status: "draft",
      notes: "",
    };
    this.milestones = [];
  };
}

export default ChangeOrderWorkflowStore;